import type { ReportDefinition } from '@olytic/foundation/reports';

import { downloadCsv } from '../objects/exportCsv';

// The report page's Export button: the server runs the definition on screen,
// unsaved edits included, and answers it as a CSV file (POST
// /api/reports/export, reports.mjs). The browser saves that response as a file
// named after the report, with the list pages' download helper.

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;
type Download = (fileName: string, csv: string) => void;

/** What a finished export shows: an info message, or the route's error text. */
export type ExportOutcome = { message: string; error?: undefined } | { error: string; message?: undefined };

/** Characters a file name cannot hold on Windows, macOS or Linux, and control characters. */
// eslint-disable-next-line no-control-regex
const UNSAFE_FILE_CHARACTERS = /[\\/:*?"<>|\u0000-\u001f]/g;

/** `<report name>.csv`, each character a file name cannot hold as `-`; `report.csv` for an empty name. */
export function reportFileName(name: string): string {
  const safe = name.replace(UNSAFE_FILE_CHARACTERS, '-').trim();
  return `${safe || 'report'}.csv`;
}

/**
 * The CSV's data rows: its records after the header line. A quoted cell may
 * hold a line break, so records are counted outside quotes; a trailing line
 * break does not start another record.
 */
export function csvDataRows(csv: string): number {
  const text = csv.replace(/^\uFEFF/, '');
  let records = 0;
  let quoted = false;
  let open = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      quoted = !quoted;
      open = true;
    } else if (!quoted && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      records += 1;
      open = false;
    } else {
      open = true;
    }
  }
  if (open) records += 1;
  return Math.max(records - 1, 0);
}

const count = (n: number) => n.toLocaleString('en-US');

/** "Exported 12 rows.", or, when the server's row cap cut the run, the cap. */
export function exportMessage(rows: number, rowCap: number | null): string {
  if (rowCap !== null) return `Exported the first ${count(rowCap)} rows, the most a report lists.`;
  return rows === 1 ? 'Exported 1 row.' : `Exported ${count(rows)} rows.`;
}

/**
 * Exports the definition as it is (POST /api/reports/export) and downloads
 * the answer as `<report name>.csv`. A refusal or failure downloads nothing
 * and gives the route's `error` text.
 */
export async function exportReport(
  fetcher: Fetch,
  definition: ReportDefinition,
  download: Download = downloadCsv,
): Promise<ExportOutcome> {
  let response: Response;
  try {
    response = await fetcher('/api/reports/export', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ definition }),
    });
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'The report could not be exported.' };
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: unknown } | null;
    return { error: typeof body?.error === 'string' ? body.error : `The report could not be exported (${response.status}).` };
  }
  const csv = await response.text();
  const capHeader = response.headers.get('x-report-row-cap');
  const rowCap = capHeader !== null && /^\d+$/.test(capHeader.trim()) ? Number(capHeader) : null;
  download(reportFileName(definition.name), csv);
  return { message: exportMessage(csvDataRows(csv), rowCap) };
}

/**
 * One export at a time: `start` does nothing (and answers false) while an
 * export is open. `onChange` hears when one starts (`exporting: true`) and
 * how it ended.
 */
export function createReportExporter(
  fetcher: Fetch,
  onChange: (state: { exporting: boolean; outcome: ExportOutcome | null }) => void,
  download: Download = downloadCsv,
): { start: (definition: ReportDefinition) => boolean } {
  let open = false;
  return {
    start(definition) {
      if (open) return false;
      open = true;
      onChange({ exporting: true, outcome: null });
      const finish = (outcome: ExportOutcome) => {
        open = false;
        onChange({ exporting: false, outcome });
      };
      exportReport(fetcher, definition, download).then(finish, (error: unknown) =>
        finish({ error: error instanceof Error ? error.message : 'The report could not be exported.' }));
      return true;
    },
  };
}
